import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { AlertCircle, FileText, Loader, RefreshCw, Upload } from 'lucide-react';
import { validateLoanDataForm } from '../../components/LoanDataForm';
import { StatusDot } from '../../components/ui/StatusDot';
import type { WorkflowJob } from '../../graphql/types';
import { writeWorkspaceParams } from '../urlState';
import { clearDraftStagedFiles, peekDraftStagedFiles } from '../draftHandoff';
import {
  ensureLoan,
  loadIntakeData,
  saveLoanFields,
  submitForExtraction,
  uploadDocument,
} from './intakeBAData';
import type { IntakeBAData } from './intakeBAData';
import {
  ACCEPTED_FORMATS_TEXT,
  ACCEPT_ATTRIBUTE,
  AUTOSAVE_DELAY_MS,
  BA_FIELD_GROUPS,
  backendFieldsChanged,
  buildChecklist,
  countMissing,
  formFromSources,
  getExtractAction,
  loadIntakeRecord,
  pickExtras,
  saveIntakeRecord,
  saveStatusLabel,
  toLoanDataFormState,
  uniqueFileName,
  validateIntakeFile,
} from './intakeBAModel';
import type { BAFieldKey, BAFormState, IntakeBARecord, SaveStatus } from './intakeBAModel';

/**
 * IntakeBA: the Borrower Analysis Intake mode, built from board `4b`.
 *
 * Two columns. Left: the loan information card with Borrower, Loan and
 * Property groups in a 2-column grid and a "Saving…" / "Saved" indicator.
 * Right: the document drop zone, the standard borrower document checklist and
 * the extract action.
 *
 * - Edits auto-save after a short pause (Requirement 5.2). Borrower name,
 *   loan number, loan amount and address go to the Loan record
 *   (`updateLoanInfo`); the other fields are kept on this device until the
 *   backend can store them.
 * - The drop zone accepts PDF, PNG, JPEG and TIFF up to 10 MB each (5.3).
 * - The checklist ticks off documents as they arrive and offers "Upload" for
 *   the missing ones (5.4).
 * - The extract action stays enabled when documents are missing and says how
 *   many (5.5). Once extraction has started it sends only files added since
 *   (5.6), using the same resubmission path as the legacy step.
 *
 * Requirements: 5.1, 5.2, 5.3, 5.4, 5.5, 5.6.
 */
export interface IntakeBAProps {
  job: WorkflowJob;
  /** Bucket for uploads. Defaults to `VITE_S3_DOCUMENTS_BUCKET`. */
  uploadBucket?: string;
  /** Receives the job returned when extraction starts. */
  onJobUpdate?: (job: WorkflowJob) => void;
  /** Opens Extract mode. Defaults to setting `mode=extract` in the URL. */
  onOpenExtract?: () => void;
  /** Receives "4 of 5 docs" for the Top_Bar Status_Pill, from the live checklist. */
  onCountsChange?: (counts: { receivedDocuments: number; requiredDocuments: number }) => void;
  /** Called after the loan, its documents or the extraction changed, so the Rail can refresh. */
  onDataChanged?: () => void;
}

interface StagedFile {
  id: string;
  /** Name the file is stored under (unique on the loan). */
  name: string;
  size: number;
  status: 'uploading' | 'completed' | 'error';
  error?: string;
}

interface Rejection {
  name: string;
  reason: string;
}

const CARD = 'rounded-xl border border-gray-200 bg-white';

// White on the BA accent #A34DF5 is 4.3:1, under the 4.5:1 text rule (13.4).
// The prototype's own hover shade, #8B2EEA, is 5.7:1, so it is the resting colour.
const EXTRACT_BUTTON =
  'inline-flex w-full items-center justify-center gap-2 rounded-[9px] bg-[#8B2EEA] px-4 py-3 text-sm font-bold text-white transition-colors hover:bg-[#7A1FD6] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#8B2EEA] focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-[#8B2EEA]';

const INPUT =
  'w-full rounded-lg border border-gray-300 bg-white px-3 py-[9px] text-sm text-gray-900 placeholder:text-gray-500 focus:border-[#834DFB] focus:outline-none focus:ring-[3px] focus:ring-[#834DFB]/15 aria-[invalid=true]:border-[#dc2626]';

function startedStatus(job: Pick<WorkflowJob, 'documentProcessingStatus'>): string {
  return String(job.documentProcessingStatus ?? '').toUpperCase();
}

function isExtractionStarted(status: string): boolean {
  return status !== '' && status !== 'NOT_STARTED' && status !== 'PENDING';
}

function formatSize(bytes: number): string {
  return bytes >= 1024 * 1024
    ? `${(bytes / (1024 * 1024)).toFixed(1)} MB`
    : `${Math.max(1, Math.round(bytes / 1024))} KB`;
}

export const IntakeBA: React.FC<IntakeBAProps> = ({
  job,
  uploadBucket = import.meta.env.VITE_S3_DOCUMENTS_BUCKET,
  onJobUpdate,
  onOpenExtract,
  onCountsChange,
  onDataChanged,
}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const dataChangedRef = useRef(onDataChanged);
  dataChangedRef.current = onDataChanged;
  const notifyDataChanged = useCallback(() => dataChangedRef.current?.(), []);

  // ── Loan, files and documents ─────────────────────────────────────────────
  const [data, setData] = useState<IntakeBAData | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [form, setForm] = useState<BAFormState | null>(null);
  const [record, setRecord] = useState<IntakeBARecord>(() => loadIntakeRecord(job.jobId));

  const latest = useRef({ data, form, record });
  latest.current = { data, form, record };

  const refresh = useCallback(async () => {
    try {
      const next = await loadIntakeData(job.jobId);
      setData(next);
      setLoadError(null);
      return next;
    } catch (err) {
      console.error('[IntakeBA] Failed to load the loan:', err);
      setLoadError('The loan could not be loaded. Check your connection and try again.');
      return null;
    }
  }, [job.jobId]);

  // The form is filled once. Later refreshes (polling) never overwrite edits.
  const lastSaved = useRef<BAFormState | null>(null);
  useEffect(() => {
    let cancelled = false;
    setForm(null);
    setData(null);
    void refresh().then((next) => {
      if (cancelled) return;
      const initial = formFromSources(next?.loan, latest.current.record.extras);
      lastSaved.current = initial;
      setForm(initial);
    });
    return () => {
      cancelled = true;
    };
  }, [refresh]);

  useEffect(() => {
    saveIntakeRecord(job.jobId, record);
  }, [job.jobId, record]);

  const status = startedStatus(job);
  const started = isExtractionStarted(status);
  const inProgress = status === 'IN_PROGRESS';

  // Files and extracted types arrive while extraction runs.
  useEffect(() => {
    if (!inProgress) return undefined;
    const timer = setInterval(() => {
      void refresh();
      notifyDataChanged();
    }, 3000);
    return () => clearInterval(timer);
  }, [inProgress, refresh, notifyDataChanged]);

  // ── Auto-save (Requirement 5.2) ───────────────────────────────────────────
  const [saveStatus, setSaveStatus] = useState<SaveStatus>('idle');
  const [borrowerError, setBorrowerError] = useState<string | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const saveSeq = useRef(0);

  const persist = useCallback(
    async (snapshot: BAFormState) => {
      const validation = validateLoanDataForm(toLoanDataFormState(snapshot));
      setBorrowerError(validation);
      if (validation) {
        // Nothing is sent while the borrower name is blank, so a half-typed
        // form never blanks the stored loan.
        setSaveStatus('idle');
        return;
      }
      const seq = ++saveSeq.current;
      setSaveStatus('saving');
      try {
        const previous = lastSaved.current;
        if (!previous || backendFieldsChanged(previous, snapshot)) {
          const loan = await ensureLoan(job.jobId, latest.current.data?.loan ?? null);
          if (!latest.current.data?.loan) {
            setData((d) => (d ? { ...d, loan } : d));
          }
          const tenantId = latest.current.data?.tenantId ?? 'default-tenant';
          await saveLoanFields(loan, tenantId, snapshot);
        }
        lastSaved.current = snapshot;
        if (seq === saveSeq.current) setSaveStatus('saved');
        // The borrower and loan number are in the Top_Bar title.
        if (!previous || backendFieldsChanged(previous, snapshot)) notifyDataChanged();
      } catch (err) {
        console.error('[IntakeBA] Auto-save failed:', err);
        if (seq === saveSeq.current) setSaveStatus('error');
      }
    },
    [job.jobId, notifyDataChanged]
  );

  const handleChange = (key: BAFieldKey, value: string) => {
    if (!form) return;
    const next = { ...form, [key]: value };
    setForm(next);
    setRecord((r) => ({ ...r, extras: pickExtras(next) }));
    setSaveStatus('saving');
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      timer.current = null;
      void persist(next);
    }, AUTOSAVE_DELAY_MS);
  };

  // Leaving Intake with an edit still waiting saves it.
  useEffect(
    () => () => {
      if (timer.current) {
        clearTimeout(timer.current);
        timer.current = null;
        if (latest.current.form) void persist(latest.current.form);
      }
    },
    [persist]
  );

  // ── Uploads (Requirements 5.3, 5.4) ───────────────────────────────────────
  // Files the draft Workspace already uploaded to this job show as Ready.
  const [staged, setStaged] = useState<StagedFile[]>(() => peekDraftStagedFiles(job.jobId));
  useEffect(() => {
    clearDraftStagedFiles(job.jobId);
  }, [job.jobId]);
  const [rejections, setRejections] = useState<Rejection[]>([]);
  const [dragOver, setDragOver] = useState(false);
  const stagedRef = useRef(staged);
  stagedRef.current = staged;
  const idCounter = useRef(0);
  const fileInput = useRef<HTMLInputElement>(null);
  const slotRef = useRef<string | undefined>(undefined);
  const [actionError, setActionError] = useState<string | null>(null);

  const patchStaged = (id: string, patch: Partial<StagedFile>) =>
    setStaged((prev) => prev.map((s) => (s.id === id ? { ...s, ...patch } : s)));

  const handleFiles = useCallback(
    async (files: File[], slot?: string) => {
      if (files.length === 0) return;
      const rejected: Rejection[] = [];
      const accepted: File[] = [];
      for (const file of files) {
        const reason = validateIntakeFile(file);
        if (reason) rejected.push({ name: file.name, reason });
        else accepted.push(file);
      }
      setRejections(rejected);
      if (accepted.length === 0) return;
      setActionError(null);

      const taken = new Set<string>([
        ...(latest.current.data?.files.map((f) => f.filename) ?? []),
        ...latest.current.record.submitted,
        ...stagedRef.current.map((s) => s.name),
      ]);
      const entries = accepted.map((file) => {
        const name = uniqueFileName(file.name, taken);
        taken.add(name);
        const entry: StagedFile = {
          id: `upload-${++idCounter.current}`,
          name,
          size: file.size,
          status: 'uploading',
        };
        return { file, entry };
      });
      setStaged((prev) => [...prev, ...entries.map((e) => e.entry)]);

      const failAll = (message: string) =>
        setStaged((prev) =>
          prev.map((s) =>
            entries.some((e) => e.entry.id === s.id) ? { ...s, status: 'error', error: message } : s
          )
        );

      if (!uploadBucket) {
        failAll('Uploads are not set up. Reload the page and try again.');
        return;
      }
      let loanId: string;
      try {
        const loan = await ensureLoan(job.jobId, latest.current.data?.loan ?? null);
        loanId = loan.loanId;
        if (!latest.current.data?.loan) setData((d) => (d ? { ...d, loan } : d));
      } catch (err) {
        console.error('[IntakeBA] Failed to create the loan:', err);
        failAll('The loan could not be created. Try again.');
        return;
      }

      for (const { file, entry } of entries) {
        try {
          await uploadDocument(file, entry.name, loanId, job, uploadBucket);
          patchStaged(entry.id, { status: 'completed' });
          if (slot) {
            setRecord((r) => ({ ...r, assignments: { ...r.assignments, [entry.name]: slot } }));
          }
        } catch (err) {
          console.error('[IntakeBA] Upload failed:', err);
          patchStaged(entry.id, { status: 'error', error: 'Upload failed. Try again.' });
        }
      }
      notifyDataChanged();
    },
    [job, uploadBucket, notifyDataChanged]
  );

  const openPicker = (slot?: string) => {
    slotRef.current = slot;
    fileInput.current?.click();
  };

  const handlePicked = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    const slot = slotRef.current;
    slotRef.current = undefined;
    e.target.value = '';
    void handleFiles(files, slot);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    void handleFiles(Array.from(e.dataTransfer.files));
  };

  // ── Checklist and extract action (Requirements 5.4 to 5.6) ────────────────
  const stagedDone = staged.filter((s) => s.status === 'completed').map((s) => s.name);
  const uploading = staged.some((s) => s.status === 'uploading');

  const known = useMemo(
    () => new Set<string>([...(data?.files.map((f) => f.filename) ?? []), ...record.submitted]),
    [data, record.submitted]
  );
  const receivedNames = useMemo(
    () => Array.from(new Set<string>([...known, ...stagedDone])),
    // stagedDone is rebuilt each render; its content is what matters.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [known, stagedDone.join('\n')]
  );
  const checklist = useMemo(
    () =>
      buildChecklist({
        fileNames: receivedNames,
        documentTypes: data?.documents.map((d) => d.documentType).filter(Boolean) ?? [],
        assignments: record.assignments,
      }),
    [receivedNames, data, record.assignments]
  );
  const missingCount = countMissing(checklist);
  const receivedCount = checklist.length - missingCount;
  const requiredCount = checklist.length;

  // Top_Bar "Intake · 4 of 5 docs": only Intake knows about documents that are
  // added but not yet extracted, so it reports the live checklist.
  const formReady = !!form;
  useEffect(() => {
    if (formReady) {
      onCountsChange?.({ receivedDocuments: receivedCount, requiredDocuments: requiredCount });
    }
  }, [formReady, receivedCount, requiredCount, onCountsChange]);

  // After extraction starts only files added since are new (5.6).
  const pendingNames = started ? stagedDone.filter((n) => !known.has(n)) : receivedNames;
  const action = getExtractAction({
    pendingCount: pendingNames.length,
    submittedCount: started ? known.size : 0,
    missingCount,
    alreadyExtracting: started,
    uploading,
  });

  const [submitting, setSubmitting] = useState(false);

  const openExtract = () => {
    if (onOpenExtract) onOpenExtract();
    else setSearchParams(writeWorkspaceParams(searchParams, { mode: 'extract' }));
  };

  const handleAction = async () => {
    if (!action.enabled || submitting) return;
    if (action.kind === 'review') {
      openExtract();
      return;
    }
    setSubmitting(true);
    setActionError(null);
    try {
      const updated = await submitForExtraction(job, started);
      if (updated) onJobUpdate?.(updated);
      const sent = new Set(pendingNames);
      setRecord((r) => ({
        ...r,
        submitted: Array.from(new Set([...r.submitted, ...pendingNames])),
      }));
      setStaged((prev) => prev.filter((s) => !sent.has(s.name)));
      await refresh();
      notifyDataChanged();
    } catch (err) {
      console.error('[IntakeBA] Failed to start extraction:', err);
      setActionError('Extraction could not be started. Check your connection and try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // ── Render ────────────────────────────────────────────────────────────────
  if (!form) {
    if (loadError) {
      return (
        <div role="alert" className="max-w-xl rounded-xl border border-red-200 bg-red-50 p-5">
          <div className="flex items-start gap-3">
            <AlertCircle size={17} strokeWidth={2} className="mt-0.5 shrink-0 text-red-600" aria-hidden="true" />
            <div>
              <p className="text-sm text-red-800">{loadError}</p>
              <button
                type="button"
                onClick={() => {
                  void refresh().then((next) => {
                    if (!next) return;
                    const initial = formFromSources(next.loan, latest.current.record.extras);
                    lastSaved.current = initial;
                    setForm(initial);
                  });
                }}
                className="mt-3 inline-flex items-center gap-2 rounded-lg bg-red-600 px-3 py-1.5 text-sm font-bold text-white hover:bg-red-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-2"
              >
                <RefreshCw size={14} strokeWidth={2} aria-hidden="true" />
                Retry
              </button>
            </div>
          </div>
        </div>
      );
    }
    return (
      <div className="flex min-h-[240px] items-center justify-center gap-2 text-sm text-gray-600" role="status">
        <Loader size={17} strokeWidth={2} className="animate-spin text-[#834DFB]" aria-hidden="true" />
        Loading loan…
      </div>
    );
  }

  const statusText = saveStatusLabel(saveStatus);

  return (
    <div
      data-testid="intake-ba"
      className="grid max-w-[1200px] grid-cols-[minmax(0,1.1fr)_minmax(0,1fr)] items-start gap-4"
    >
      {/* Loan information */}
      <section aria-labelledby="ba-loan-info" className={`${CARD} flex flex-col gap-4 p-[18px]`}>
        <div className="flex items-center justify-between gap-3">
          <h2 id="ba-loan-info" className="text-base font-bold text-gray-900">
            Loan information
          </h2>
          <span className="flex items-center gap-2">
            <span
              data-testid="save-status"
              aria-live="polite"
              className={`text-xs font-bold ${
                saveStatus === 'saved'
                  ? 'text-[#15803d]'
                  : saveStatus === 'error'
                    ? 'text-[#991b1b]'
                    : 'text-gray-600'
              }`}
            >
              {statusText}
            </span>
            {saveStatus === 'error' && (
              <button
                type="button"
                onClick={() => void persist(form)}
                className="rounded text-xs font-bold text-[#5A21C9] hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-[#834DFB]"
              >
                Try again
              </button>
            )}
          </span>
        </div>

        {BA_FIELD_GROUPS.map((group) => (
          <div key={group.name} role="group" aria-label={group.name}>
            <h3 className="mb-2 text-[11px] font-bold uppercase tracking-[0.1em] text-gray-500">
              {group.name}
            </h3>
            <div className="grid grid-cols-2 gap-x-3 gap-y-2.5">
              {group.fields.map((field) => {
                const id = `ba-${field.key}`;
                const invalid = field.key === 'borrowerName' && !!borrowerError;
                return (
                  <div key={field.key} className="flex min-w-0 flex-col gap-[5px]">
                    <label htmlFor={id} className="text-[13px] font-bold text-gray-700">
                      {field.label}
                      {field.key === 'borrowerName' && (
                        <span className="ml-0.5 text-[#dc2626]" aria-hidden="true">
                          *
                        </span>
                      )}
                    </label>
                    <input
                      id={id}
                      type="text"
                      value={form[field.key]}
                      placeholder={field.placeholder}
                      onChange={(e) => handleChange(field.key, e.target.value)}
                      aria-required={field.key === 'borrowerName' ? true : undefined}
                      aria-invalid={invalid || undefined}
                      aria-describedby={invalid ? `${id}-error` : undefined}
                      className={INPUT}
                    />
                    {invalid && (
                      <p id={`${id}-error`} role="alert" className="text-xs text-[#991b1b]">
                        {borrowerError}. Nothing is saved until it is filled in.
                      </p>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </section>

      {/* Documents */}
      <div className="flex min-w-0 flex-col gap-4">
        <input
          ref={fileInput}
          type="file"
          multiple
          accept={ACCEPT_ATTRIBUTE}
          onChange={handlePicked}
          className="hidden"
          tabIndex={-1}
          aria-label="Choose documents"
          data-testid="file-input"
        />

        <div
          data-testid="dropzone"
          onDragOver={(e) => {
            e.preventDefault();
            setDragOver(true);
          }}
          onDragLeave={() => setDragOver(false)}
          onDrop={handleDrop}
          className={`rounded-xl border-2 border-dashed p-[26px] text-center transition-colors ${
            dragOver ? 'border-[#834DFB] bg-[#F5F3FF]' : 'border-[#DDD0FF] bg-white'
          }`}
        >
          <p className="text-base font-bold text-gray-900">Drop the loan&apos;s documents</p>
          <p className="mt-1 text-[13px] text-gray-600">
            {ACCEPTED_FORMATS_TEXT} · up to 10 MB each
          </p>
          <button
            type="button"
            onClick={() => openPicker()}
            className="mt-3 inline-flex items-center gap-2 rounded-lg border border-[#DDD0FF] bg-white px-3 py-1.5 text-sm font-bold text-[#5A21C9] hover:bg-[#F5F3FF] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#834DFB] focus-visible:ring-offset-2"
          >
            <Upload size={14} strokeWidth={2} aria-hidden="true" />
            Choose files
          </button>
        </div>

        {rejections.length > 0 && (
          <div role="alert" className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-[13px] text-red-800">
            <p className="font-bold">
              {rejections.length === 1 ? '1 file was not added' : `${rejections.length} files were not added`}
            </p>
            <ul className="mt-1 list-disc pl-5">
              {rejections.map((r, i) => (
                <li key={`${r.name}-${i}`}>
                  <span className="font-bold">{r.name}</span>: {r.reason}
                </li>
              ))}
            </ul>
          </div>
        )}

        {staged.length > 0 && (
          <ul data-testid="staged-files" aria-label="Files added" className={`${CARD} divide-y divide-gray-100`}>
            {staged.map((s) => (
              <li key={s.id} className="flex items-center gap-2.5 px-3.5 py-2 text-sm">
                <FileText size={14} strokeWidth={2} className="shrink-0 text-gray-500" aria-hidden="true" />
                <span className="min-w-0 flex-1 truncate text-gray-700" title={s.name}>
                  {s.name}
                </span>
                <span className="shrink-0 text-xs text-gray-600">{formatSize(s.size)}</span>
                {s.status === 'uploading' && (
                  <span className="inline-flex shrink-0 items-center gap-1 text-xs font-bold text-gray-600">
                    <Loader size={12} strokeWidth={2} className="animate-spin" aria-hidden="true" />
                    Uploading
                  </span>
                )}
                {s.status === 'completed' && (
                  <span className="shrink-0 text-xs font-bold text-[#15803d]">Ready</span>
                )}
                {s.status === 'error' && (
                  <>
                    <span className="shrink-0 text-xs font-bold text-[#991b1b]">{s.error}</span>
                    <button
                      type="button"
                      onClick={() => setStaged((prev) => prev.filter((x) => x.id !== s.id))}
                      className="shrink-0 rounded text-xs font-bold text-[#5A21C9] hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-[#834DFB]"
                      aria-label={`Dismiss ${s.name}`}
                    >
                      Dismiss
                    </button>
                  </>
                )}
              </li>
            ))}
          </ul>
        )}

        <section
          data-testid="checklist"
          aria-labelledby="ba-checklist"
          className={`${CARD} overflow-hidden`}
        >
          <div className="flex items-center justify-between gap-2 border-b border-gray-100 bg-[#f9fafb] px-3.5 py-2.5">
            <h2 id="ba-checklist" className="text-[13px] font-bold text-gray-700">
              Standard borrower documents
            </h2>
            <span className="text-xs text-gray-600">
              {receivedCount} of {checklist.length} received
            </span>
          </div>
          <ul>
            {checklist.map((row) => (
              <li
                key={row.id}
                data-testid={`checklist-${row.id}`}
                data-received={row.received}
                className="flex items-center gap-2.5 border-b border-[#f9fafb] px-3.5 py-[9px] last:border-b-0"
              >
                <StatusDot
                  tone={row.received ? 'success' : 'idle'}
                  size={10}
                  label={row.received ? 'Received' : 'Missing'}
                />
                <span className="min-w-0 flex-1 text-sm text-gray-700">{row.label}</span>
                {row.received ? (
                  <span className="text-xs font-bold text-[#15803d]">Received</span>
                ) : (
                  <button
                    type="button"
                    onClick={() => openPicker(row.id)}
                    aria-label={`Upload ${row.label}`}
                    className="rounded text-xs font-bold text-[#6E2EF0] hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-[#834DFB]"
                  >
                    Upload
                  </button>
                )}
              </li>
            ))}
          </ul>
        </section>

        {actionError && (
          <p role="alert" className="text-[13px] text-[#991b1b]">
            {actionError}
          </p>
        )}

        <button
          type="button"
          data-testid="extract-action"
          onClick={() => void handleAction()}
          disabled={!action.enabled || submitting}
          title={action.title}
          className={EXTRACT_BUTTON}
        >
          {submitting ? (
            <>
              <Loader size={14} strokeWidth={2} className="animate-spin" aria-hidden="true" />
              Starting extraction
            </>
          ) : (
            action.label
          )}
        </button>

        {inProgress && (
          <p data-testid="extraction-note" role="status" className="text-[13px] text-gray-600">
            Extraction is running. You can add more documents; only the new files are extracted.
          </p>
        )}
      </div>
    </div>
  );
};

export default IntakeBA;
